import { GVA_API_URL } from '../config/serviceConfig';

/**
 * 解析机构媒体 URL（相对路径拼 GVA）；空串返回空，不做默认图回落。
 */
export function resolveBrandMediaUrl(url) {
  if (!url) return '';
  if (
    url.startsWith('http://') ||
    url.startsWith('https://') ||
    url.startsWith('data:') ||
    url.startsWith('/avatars/')
  ) {
    return url;
  }
  const base = String(GVA_API_URL || '').replace(/\/$/, '');
  const path = url.startsWith('/') ? url : `/${url}`;
  return `${base}${path}`;
}

function parseHex(color) {
  if (!color || typeof color !== 'string') return null;
  const m = /^#([0-9a-fA-F]{6})$/.exec(color.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function toHex({ r, g, b }) {
  return `#${[r, g, b]
    .map((x) => Math.max(0, Math.min(255, x)).toString(16).padStart(2, '0'))
    .join('')}`;
}

function mix(a, b, t) {
  return {
    r: Math.round(a.r + (b.r - a.r) * t),
    g: Math.round(a.g + (b.g - a.g) * t),
    b: Math.round(a.b + (b.b - a.b) * t),
  };
}

function rgba(c, a) {
  return `rgba(${c.r}, ${c.g}, ${c.b}, ${a})`;
}

function cssUrl(url) {
  return `url("${String(url).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}")`;
}

/**
 * 由机构 branding 派生群聊 CSS 变量（--gc-* / --expo-*）与背景图。
 * 无定制时返回 {}，不影响默认样式。
 */
export function buildGroupChatThemeStyle(branding) {
  if (!branding || typeof branding !== 'object') return {};

  const style = {};
  const bgRaw = branding.bgImageUrl && String(branding.bgImageUrl).trim();
  const themeRaw = branding.themeColor && String(branding.themeColor).trim();

  if (bgRaw) {
    const resolved = resolveBrandMediaUrl(bgRaw);
    if (resolved) {
      style['--gc-bg-image'] = cssUrl(resolved);
      // 展示模式中心数字人默认 mix-blend-mode:screen，依赖深色底；
      // 有亮色机构背景时改为 normal，配合黑底抠图，避免角色被冲成半透明。
      style['--gc-dh-blend'] = 'normal';
      // 启用舞台追光衬底，遮住抠图误伤的深色发丝透底。
      style['--gc-dh-backdrop'] = '1';
      style['--gc-dh-backdrop-display'] = 'block';
    }
  }

  const accent = parseHex(themeRaw);
  if (!accent) return style;

  const slate900 = { r: 15, g: 23, b: 42 };
  const expoDark = { r: 7, g: 17, b: 31 };
  const cardBase = { r: 30, g: 41, b: 59 };
  const black = { r: 0, g: 0, b: 0 };
  const cyanHint = { r: 100, g: 255, b: 218 };

  const panel = mix(slate900, accent, 0.12);
  const bg = mix(expoDark, accent, 0.08);
  const card = mix(cardBase, accent, 0.15);
  const strong = mix(accent, black, 0.22);

  style['--gc-blue'] = toHex(accent);
  style['--gc-blue-strong'] = toHex(strong);
  style['--gc-blue-soft'] = rgba(accent, 0.14);
  style['--gc-cyan'] = toHex(mix(accent, cyanHint, 0.35));
  style['--gc-bg'] = toHex(bg);
  style['--gc-panel'] = toHex(panel);
  style['--gc-card'] = toHex(card);
  style['--gc-card-hover'] = toHex(mix(card, accent, 0.12));
  style['--gc-border'] = rgba(accent, 0.22);
  style['--gc-border-strong'] = rgba(accent, 0.35);

  style['--expo-blue'] = toHex(accent);
  style['--expo-blue-dim'] = rgba(accent, 0.56);
  style['--expo-glow'] = rgba(accent, 0.18);
  style['--expo-line'] = rgba(accent, 0.14);
  style['--expo-bg'] = toHex(mix(expoDark, accent, 0.1));
  style['--expo-panel'] = rgba(mix(expoDark, accent, 0.15), 0.72);
  style['--expo-panel-solid'] = toHex(mix(expoDark, accent, 0.12));

  return style;
}

/**
 * 浅层机构主题覆盖：仅背景图 + 强调色，供综合训练 / 专项训练 / 专家研讨等
 * 尚未做全量 CSS 变量化的面板使用。变量名按 prefix 区分，避免与群聊的
 * --gc-* / --expo-* 互相覆盖。无 branding 时返回 {}，不影响默认样式。
 *
 * @param {object} branding  currentUser.branding，含 bgImageUrl / themeColor
 * @param {string} prefix    CSS 变量前缀，如 'dp'（研讨）、'plaza'、'ccma'、'portal'
 */
export function buildAccentThemeStyle(branding, prefix) {
  if (!branding || typeof branding !== 'object' || !prefix) return {};

  const style = {};
  const bgRaw = branding.bgImageUrl && String(branding.bgImageUrl).trim();
  const themeRaw = branding.themeColor && String(branding.themeColor).trim();

  if (bgRaw) {
    const resolved = resolveBrandMediaUrl(bgRaw);
    if (resolved) {
      style[`--${prefix}-bg-image`] = cssUrl(resolved);
    }
  }

  const accent = parseHex(themeRaw);
  if (accent) {
    const black = { r: 0, g: 0, b: 0 };
    style[`--${prefix}-accent`] = toHex(accent);
    style[`--${prefix}-accent-strong`] = toHex(mix(accent, black, 0.22));
    style[`--${prefix}-accent-soft`] = rgba(accent, 0.14);
  }

  return style;
}
